/**
 * Client Inventory Picker Dialog
 *
 * Shows client's actual inventory (like InventoryPage) for product selection
 * Users can see location-specific stock and select products directly
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Card } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Alert, AlertDescription } from '../../ui/alert';
import { Checkbox } from '../../ui/checkbox';
import { Switch } from '../../ui/switch';
import { Label } from '../../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '../../ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '../../ui/collapsible';
import {
  X,
  Search,
  ChevronDown,
  ChevronUp,
  Plus,
  Minus,
  Loader2,
  History
} from 'lucide-react';
import inventoryService from '../../../services/inventoryService';
import InventoryHistoryDialog from '../../InventoryHistoryDialog';

function ClientInventoryPickerDialog({ open, onClose, clientId, clientName, onConfirm }) {
  const [loading, setLoading] = useState(false);
  const [inventory, setInventory] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  const [excludeZeroStock, setExcludeZeroStock] = useState(true);
  const [selectedProducts, setSelectedProducts] = useState({}); // { groupKey: { total, locations: [{location, quantity}] } } - groupKey = product_code
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [error, setError] = useState(null);
  const [historyDialog, setHistoryDialog] = useState({ open: false, product: null });

  // Load client inventory on open
  const loadClientInventory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Use global inventory view (cross-client stock is allowed)
      // Pass searchText as product_id filter for server-side filtering
      // Exclude zero stock items for cleaner picker.
      // Rows use the default inventory grouping (product + client +
      // location), same as the inventory page — no per-spec split.
      const filters = {};
      if (searchText.trim()) {
        filters.product_id = searchText.trim();
      }
      const response = await inventoryService.getInventory(1, 1000, 'name', 'asc', filters, null, excludeZeroStock);

      // Group inventory by product code, tracking each location+source_client separately
      const grouped = {};
      response.inventory?.forEach(item => {
        const code = item.product_code || item.product_id;
        const groupKey = code;
        if (!grouped[groupKey]) {
          grouped[groupKey] = {
            groupKey,
            product_code: code,
            product_name: item.product_name || item.name,
            specification: item.specification,
            unit: item.unit,
            category: item.category,
            locations: [],
            locationMap: {}, // Track by location+source_client_id
            totalAvailable: 0,
            totalFrozen: 0
          };
        }

        const locationName = item.location || 'main';
        const sourceClientId = item.source_client_id || item.client_id || 'unknown';
        const locationKey = `${locationName}|${sourceClientId}`;
        // Use NET available (remaining - reserved/frozen) so stock frozen for
        // sales orders cannot be allocated here. available_quantity is always
        // returned by /api/inventory; fall back to remaining for safety.
        const remaining = item.remaining_quantity || 0;
        const qty = Math.max(0, item.available_quantity ?? remaining);
        const frozenQty = Math.max(0, remaining - qty);

        // Zero-stock filter: the backend only drops rows with remaining == 0,
        // so rows that are fully reserved/frozen (net available 0) still
        // arrive. Honor the toggle here by skipping locations with no
        // allocatable stock.
        if (excludeZeroStock && qty <= 0) {
          return;
        }

        // Check if this location+source_client combo already exists
        if (grouped[groupKey].locationMap[locationKey]) {
          // Merge: add quantity to existing entry
          grouped[groupKey].locationMap[locationKey].available_quantity += qty;
          grouped[groupKey].locationMap[locationKey].frozen_quantity += frozenQty;
          // Keep the most recent data_freshness and last_movement
          if (item.last_movement?.created_at > grouped[groupKey].locationMap[locationKey].last_movement?.created_at) {
            grouped[groupKey].locationMap[locationKey].data_freshness = item.data_freshness;
            grouped[groupKey].locationMap[locationKey].last_movement = item.last_movement;
          }
        } else {
          // New location+source_client entry
          grouped[groupKey].locationMap[locationKey] = {
            location: locationName,
            source_client_id: sourceClientId,
            source_client_name: item.source_client_name || item.client_name || '未知客户',
            specification: item.specification || '',
            available_quantity: qty,
            frozen_quantity: frozenQty,
            data_freshness: item.data_freshness,
            last_movement: item.last_movement
          };
        }

        grouped[groupKey].totalAvailable += qty;
        grouped[groupKey].totalFrozen += frozenQty;
      });

      // Convert locationMap to locations array
      Object.values(grouped).forEach(product => {
        product.locations = Object.values(product.locationMap);
        delete product.locationMap; // Clean up temporary map
      });

      // Backend already filters rows with remaining == 0 via exclude_zero_stock;
      // additionally drop groups left with no allocatable stock after the
      // zero-available location filtering above.
      const inventoryArray = excludeZeroStock
        ? Object.values(grouped).filter(p => p.totalAvailable > 0)
        : Object.values(grouped);
      setInventory(inventoryArray);
      setFilteredInventory(inventoryArray);
    } catch (err) {
      setError(err.response?.data?.error || '加载库存失败');
      console.error('Error loading client inventory:', err);
    } finally {
      setLoading(false);
    }
  }, [searchText, excludeZeroStock]);

  useEffect(() => {
    if (open && clientId) {
      loadClientInventory();
    }
  }, [open, clientId, loadClientInventory]);

  // Debounced search - trigger API call when searchText changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (open && clientId) {
        loadClientInventory();
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchText, open, clientId, loadClientInventory]);

  // Filter inventory based on location (search is now server-side)
  useEffect(() => {
    let filtered = inventory;

    // Location filter
    if (locationFilter && locationFilter !== 'all') {
      filtered = filtered.filter(p =>
        p.locations.some(loc => loc.location === locationFilter)
      );
    }

    setFilteredInventory(filtered);
  }, [locationFilter, inventory]);

  // Get unique locations for filter
  const availableLocations = [...new Set(
    inventory.flatMap(p => p.locations.map(loc => loc.location))
  )].sort();

  // Toggle row expansion (productKey = product_code)
  const toggleRow = (productKey) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(productKey)) {
      newExpanded.delete(productKey);
    } else {
      newExpanded.add(productKey);
    }
    setExpandedRows(newExpanded);
  };

  // Handle product selection (keyed by product_code)
  const handleProductSelect = (product, checked) => {
    const newSelected = { ...selectedProducts };
    const productKey = product.groupKey;

    if (checked) {
      // Auto-expand when selected
      setExpandedRows(prev => new Set([...prev, productKey]));

      newSelected[productKey] = {
        product_code: product.product_code,
        product_name: product.product_name,
        specification: product.specification,
        unit: product.unit,
        totalAvailable: product.totalAvailable,
        locations: product.locations,
        totalSelected: 0,
        locationAllocations: []
      };
    } else {
      delete newSelected[productKey];
    }

    setSelectedProducts(newSelected);
  };

  // Handle quantity change for specific location
  // locKey format: "location|source_client_id" to uniquely identify stock source
  const handleLocationQuantityChange = (productKey, locKey, quantity) => {
    const newSelected = { ...selectedProducts };
    if (!newSelected[productKey]) return;

    // Parse locKey to get location and source_client_id
    const [location, sourceClientId] = locKey.split('|');

    const qty = parseFloat(quantity) || 0;
    // Find location data matching location + source_client_id
    const locationData = newSelected[productKey].locations.find(l =>
      l.location === location &&
      (l.source_client_id || 'unknown') === sourceClientId
    );

    if (!locationData) return;

    // Validate against available
    if (qty > locationData.available_quantity) {
      return; // Don't allow over-allocation
    }

    // Update or add location allocation (now keyed by locKey)
    const allocations = newSelected[productKey].locationAllocations || [];
    const existingIndex = allocations.findIndex(a => a.locKey === locKey);

    if (qty > 0) {
      if (existingIndex >= 0) {
        allocations[existingIndex].quantity = qty;
      } else {
        // Include source_client_id in allocation for backend transfer logic
        allocations.push({
          locKey,
          location,
          source_client_id: sourceClientId !== 'unknown' ? sourceClientId : null,
          source_client_name: locationData.source_client_name,
          specification: locationData.specification || null,
          quantity: qty
        });
      }
    } else {
      // Remove if quantity is 0
      if (existingIndex >= 0) {
        allocations.splice(existingIndex, 1);
      }
    }

    newSelected[productKey].locationAllocations = allocations;
    newSelected[productKey].totalSelected = allocations.reduce((sum, a) => sum + a.quantity, 0);

    setSelectedProducts(newSelected);
  };

  // Get selected quantity for a location (using locKey: location|source_client_id)
  const getLocationQuantity = (productKey, locKey) => {
    const product = selectedProducts[productKey];
    if (!product) return 0;
    const alloc = product.locationAllocations?.find(a => a.locKey === locKey);
    return alloc?.quantity || 0;
  };

  // Handle confirm
  const handleConfirm = () => {
    const productsToAdd = Object.values(selectedProducts)
      .filter(p => p.totalSelected > 0)
      .map(p => ({
        product_id: p.product_code, // Required for backend - use product_code as product_id
        product_code: p.product_code,
        name: p.product_name,
        product_name: p.product_name,
        specification: p.specification,
        unit: p.unit,
        quantity_planned: p.totalSelected,
        quantity_actual: p.totalSelected,
        quantity: p.totalSelected,
        locations: p.locations,
        locationAllocations: p.locationAllocations,
        totalAvailable: p.totalAvailable,
        available_quantity: p.totalAvailable
      }));

    onConfirm(productsToAdd);
    handleClose();
  };

  const handleClose = () => {
    setSearchText('');
    setLocationFilter('');
    setSelectedProducts({});
    setExpandedRows(new Set());
    onClose();
  };

  const selectedCount = Object.values(selectedProducts).filter(p => p.totalSelected > 0).length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle>选择产品 - 客户库存</DialogTitle>
              <p className="text-sm text-muted-foreground mt-1">{clientName}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Search and Filters */}
          <div className="flex gap-2 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="搜索产品编码、名称、规格..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="库位筛选" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部库位</SelectItem>
                {availableLocations.map(loc => (
                  <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2 px-2">
              <Switch
                id="exclude-zero-stock"
                checked={excludeZeroStock}
                onCheckedChange={setExcludeZeroStock}
              />
              <Label htmlFor="exclude-zero-stock" className="text-sm whitespace-nowrap cursor-pointer">
                隐藏零库存
              </Label>
            </div>

            <Badge variant={selectedCount > 0 ? 'default' : 'outline'} className="flex items-center px-3">
              已选: {selectedCount}
            </Badge>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <Card className="flex-1 overflow-auto border">
              <table className="w-full text-sm">
                <thead className="bg-muted sticky top-0">
                  <tr className="border-b">
                    <th className="p-3 w-12"></th>
                    <th className="text-left p-3 font-medium">产品编码</th>
                    <th className="text-left p-3 font-medium">规格</th>
                    <th className="text-right p-3 font-medium">总库存</th>
                    <th className="text-right p-3 font-medium">已选数量</th>
                    <th className="p-3 w-20 font-medium">库位明细</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInventory.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center p-8">
                        <span className="text-sm text-muted-foreground">暂无库存数据</span>
                      </td>
                    </tr>
                  ) : (
                    filteredInventory.map((product, productIndex) => {
                      const productKey = product.groupKey;
                      const isSelected = !!selectedProducts[productKey];
                      const isExpanded = expandedRows.has(productKey);
                      const totalSelected = selectedProducts[productKey]?.totalSelected || 0;

                      return (
                        <Collapsible
                          key={productKey || productIndex}
                          open={isExpanded}
                          onOpenChange={() => toggleRow(productKey)}
                          asChild
                        >
                          <React.Fragment>
                            {/* Main Row */}
                            <tr
                              className={`border-b hover:bg-muted cursor-pointer ${isSelected ? 'bg-primary/10' : ''}`}
                            >
                              <td className="p-3">
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={(checked) => handleProductSelect(product, checked)}
                                />
                              </td>
                              <td className="p-3">
                                <div>
                                  <span className="font-medium">{product.product_code}</span>
                                  <span className="block text-xs text-muted-foreground">{product.product_name}</span>
                                </div>
                              </td>
                              <td className="p-3">
                                <span className="text-sm">{product.specification || '-'}</span>
                              </td>
                              <td className="p-3 text-right">
                                <Badge variant={product.totalAvailable > 0 ? 'default' : 'outline'}>
                                  {product.totalAvailable} {product.unit || '支'}
                                </Badge>
                                {product.totalFrozen > 0 && (
                                  <span className="block text-xs text-blue-600 mt-1">
                                    冻结 {product.totalFrozen} {product.unit || '支'}
                                  </span>
                                )}
                              </td>
                              <td className="p-3 text-right">
                                {isSelected && totalSelected > 0 && (
                                  <Badge variant="default">
                                    {totalSelected} {product.unit || '支'}
                                  </Badge>
                                )}
                              </td>
                              <td className="p-3">
                                <CollapsibleTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8">
                                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                  </Button>
                                </CollapsibleTrigger>
                              </td>
                            </tr>

                            {/* Location Details */}
                            <tr>
                              <td colSpan={6} className="p-0">
                                <CollapsibleContent>
                                  <div className="p-4 bg-muted">
                                    <span className="text-xs text-muted-foreground block mb-2">库位明细：</span>
                                    <table className="w-full text-sm">
                                      <thead>
                                        <tr className="border-b">
                                          <th className="text-left p-2 font-medium">库位</th>
                                          <th className="text-left p-2 font-medium">规格</th>
                                          <th className="text-left p-2 font-medium">来源客户</th>
                                          <th className="text-right p-2 font-medium">可用数量</th>
                                          <th className="text-center p-2 font-medium w-16">历史</th>
                                          <th className="text-right p-2 font-medium">选择数量</th>
                                          <th className="p-2 w-24"></th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {product.locations.map((loc, locIndex) => {
                                          // Use location + source_client_id as unique key
                                          const locKey = `${loc.location}|${loc.source_client_id || 'unknown'}`;
                                          const selectedQty = getLocationQuantity(product.groupKey, locKey);
                                          const maxQty = loc.available_quantity;
                                          const locationKey = `${product.groupKey}_${locKey}_${locIndex}`;

                                          return (
                                            <tr key={locationKey} className="border-b">
                                              <td className="p-2">
                                                <span className="font-medium">{loc.location}</span>
                                              </td>
                                              <td className="p-2">
                                                <span className="text-muted-foreground">{loc.specification || product.specification || '-'}</span>
                                              </td>
                                              <td className="p-2">
                                                <span className="text-muted-foreground">{loc.source_client_name || '未知'}</span>
                                              </td>
                                              <td className="p-2 text-right">
                                                <span>{loc.available_quantity} {product.unit || '支'}</span>
                                                {loc.frozen_quantity > 0 && (
                                                  <Badge variant="outline" className="ml-2 border-blue-400 text-blue-700 bg-blue-50 text-xs">
                                                    冻结 {loc.frozen_quantity}
                                                  </Badge>
                                                )}
                                              </td>
                                              <td className="p-2 text-center">
                                                <TooltipProvider>
                                                  <Tooltip>
                                                    <TooltipTrigger asChild>
                                                      <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-7 w-7"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setHistoryDialog({
                                                            open: true,
                                                            product: {
                                                              _id: product.product_code,
                                                              product_id: product.product_code,
                                                              product_code: product.product_code,
                                                              name: product.product_name,
                                                              specification: product.specification,
                                                              unit: product.unit,
                                                              location: loc.location
                                                            }
                                                          });
                                                        }}
                                                      >
                                                        <History className="h-4 w-4" />
                                                      </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>查看库存历史</TooltipContent>
                                                  </Tooltip>
                                                </TooltipProvider>
                                              </td>
                                              <td className="p-2 text-right">
                                                <Input
                                                  type="number"
                                                  value={selectedQty || ''}
                                                  onChange={(e) => handleLocationQuantityChange(
                                                    product.groupKey,
                                                    locKey,
                                                    e.target.value
                                                  )}
                                                  disabled={!isSelected}
                                                  className={`w-24 text-right ${selectedQty > maxQty ? 'border-red-500' : ''}`}
                                                  min={0}
                                                  max={maxQty}
                                                  step={1}
                                                />
                                                {selectedQty > maxQty && (
                                                  <span className="text-xs text-destructive">超出库存</span>
                                                )}
                                              </td>
                                              <td className="p-2">
                                                <div className="flex gap-1">
                                                  <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8"
                                                    disabled={!isSelected || selectedQty >= maxQty}
                                                    onClick={() => handleLocationQuantityChange(
                                                      product.groupKey,
                                                      locKey,
                                                      Math.min(selectedQty + 10, maxQty)
                                                    )}
                                                  >
                                                    <Plus className="h-4 w-4" />
                                                  </Button>
                                                  <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8"
                                                    disabled={!isSelected || selectedQty <= 0}
                                                    onClick={() => handleLocationQuantityChange(
                                                      product.groupKey,
                                                      locKey,
                                                      Math.max(selectedQty - 10, 0)
                                                    )}
                                                  >
                                                    <Minus className="h-4 w-4" />
                                                  </Button>
                                                </div>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                </CollapsibleContent>
                              </td>
                            </tr>
                          </React.Fragment>
                        </Collapsible>
                      );
                    })
                  )}
                </tbody>
              </table>
            </Card>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            取消
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={selectedCount === 0}
          >
            确认添加 ({selectedCount}个产品)
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Inventory History Dialog */}
      <InventoryHistoryDialog
        open={historyDialog.open}
        onClose={() => setHistoryDialog({ open: false, product: null })}
        productId={historyDialog.product?.product_id || historyDialog.product?.product_code}
        productInfo={historyDialog.product}
      />
    </Dialog>
  );
}

export default ClientInventoryPickerDialog;
